// src/features/fiscalization/PoliciesTab.tsx
// Fiscal policies: which provider/point/taxation system applies to a scope
// entity, and whether receipts are issued automatically.
// Covers: ListFiscalPolicies, CreateFiscalPolicy, GetFiscalPolicy (via list
// item), UpdateFiscalPolicy, DeleteFiscalPolicy, ResolveFiscalContext
// (preview), GetFiscalPolicyByEntity (lookup by entity).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useFiscalization, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { FiscalPolicy } from '@core/types/fiscalization';
import { errMessage } from './format';

interface PolicyForm {
  name: string;
  description: string;
  scope_entity_id: string;
  scope_entity_type: string;
  default_provider_id: string;
  country_code: string;
  auto_fiscalize: boolean;
}

const EMPTY: PolicyForm = { name: '', description: '', scope_entity_id: '', scope_entity_type: 'tenant', default_provider_id: '', country_code: '', auto_fiscalize: true };

export const PoliciesTab = observer(() => {
  const store = useFiscalization();
  const { t } = useI18n();
  const [editing, setEditing] = useState<FiscalPolicy | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<PolicyForm>(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<FiscalPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ points: number; providers: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Lookup by entity
  const [lookupType, setLookupType] = useState('');
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState<FiscalPolicy | null | 'none'>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    store.ListFiscalPolicies({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (p: FiscalPolicy) => {
    setEditing(p);
    setForm({
      name: p.name ?? '', description: p.description ?? '', scope_entity_id: p.scope_entity?.id ?? '',
      scope_entity_type: p.scope_entity_type ?? 'tenant', default_provider_id: p.default_provider?.id ?? '',
      country_code: p.country_code ?? '', auto_fiscalize: p.auto_fiscalize ?? true,
    });
    setFormOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (editing?.entity?.id) {
        await store.UpdateFiscalPolicy(editing.entity.id, {
          policy: { ...editing, name: form.name, description: form.description, auto_fiscalize: form.auto_fiscalize } as any,
          field_mask: [],
        } as any);
      } else {
        await store.CreateFiscalPolicy({
          entity: {} as any,
          name: form.name,
          description: form.description,
          scope_entity: { id: form.scope_entity_id } as any,
          scope_entity_type: form.scope_entity_type,
          default_provider: { id: form.default_provider_id } as any,
          default_taxation_system: 'TAXATION_SYSTEM_UNSPECIFIED',
          country_code: form.country_code,
          auto_fiscalize: form.auto_fiscalize,
          auto_refund_receipt: true,
          delivery_method: 'RECEIPT_DELIVERY_METHOD_EMAIL',
          metadata: {},
        } as any);
      }
      setFormOpen(false);
    } catch (err) {
      setError(errMessage(err, t('fiscalization.policies.saveError')));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    await store.DeleteFiscalPolicy(deleteTarget.entity.id);
    setDeleteTarget(null);
  };

  const handlePreview = async (p: FiscalPolicy) => {
    if (!p.scope_entity) return;
    setPreviewFor(p.entity?.id ?? null);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await store.ResolveFiscalContext({ scope_entity: p.scope_entity } as any);
      setPreview({ points: res.available_fiscal_points?.points?.length ?? 0, providers: res.available_providers?.providers?.length ?? 0 });
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupType.trim() || !lookupId.trim()) return;
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const policy = await store.GetFiscalPolicyByEntity(lookupType.trim(), lookupId.trim());
      setLookupResult(policy ?? 'none');
    } catch {
      setLookupResult('none');
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3 rounded-md border border-border p-4">
        <h3 className="text-sm font-medium text-foreground">{t('fiscalization.policies.lookupTitle')}</h3>
        <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-2">
          <Input placeholder={t('fiscalization.policies.lookupType')} value={lookupType} onChange={(e) => setLookupType(e.target.value)} fullWidth={false} className="flex-1" />
          <Input placeholder={t('fiscalization.policies.lookupId')} value={lookupId} onChange={(e) => setLookupId(e.target.value)} fullWidth={false} className="flex-1" />
          <Button type="submit" variant="secondary" size="sm" isLoading={lookupLoading}>{t('common.search')}</Button>
        </form>
        {lookupResult === 'none' && <p className="text-sm text-muted-foreground">{t('fiscalization.policies.lookupNone')}</p>}
        {lookupResult && lookupResult !== 'none' && (
          <p className="text-sm text-foreground">{t('fiscalization.policies.lookupFound', { name: lookupResult.name })}</p>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('fiscalization.policies.subtitle')}</p>
        <Button size="sm" onClick={openCreate}>{t('fiscalization.policies.create')}</Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('common.name')}</TableHead>
            <TableHead>{t('fiscalization.policies.scope')}</TableHead>
            <TableHead>{t('fiscalization.policies.country')}</TableHead>
            <TableHead>{t('fiscalization.policies.autoFiscalize')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {store.isLoading ? (
            <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
          ) : store.items.length === 0 ? (
            <TableEmpty colSpan={5} message={t('fiscalization.policies.empty')} />
          ) : (
            store.items.map((p) => (
              <TableRow key={p.entity?.id}>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.scope_entity_type || p.scope_entity?.type_name || '--'}</TableCell>
                <TableCell>{p.country_code || '--'}</TableCell>
                <TableCell>{p.auto_fiscalize ? <Badge variant="success" size="sm">{t('common.yes')}</Badge> : <Badge variant="secondary" size="sm">{t('common.no')}</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end items-center gap-2">
                    {previewFor === p.entity?.id && preview && (
                      <span className="text-xs text-muted-foreground mr-1">{t('fiscalization.policies.previewResult', { points: preview.points, providers: preview.providers })}</span>
                    )}
                    <Button variant="ghost" size="sm" isLoading={previewLoading && previewFor === p.entity?.id} onClick={() => handlePreview(p)}>{t('fiscalization.policies.preview')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)}>{t('common.remove')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-8">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setFormOpen(false)} aria-hidden="true" />
          <form onSubmit={submit} role="dialog" aria-modal="true" className="relative mx-4 w-full max-w-md rounded-lg border border-border bg-background p-5 shadow-xl space-y-4">
            <h3 className="text-base font-semibold text-foreground">{editing ? t('fiscalization.policies.edit') : t('fiscalization.policies.create')}</h3>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Input label={t('common.name')} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
            <Input label={t('common.description')} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            {!editing && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Input label={t('fiscalization.policies.scopeEntityId')} value={form.scope_entity_id} onChange={(e) => setForm((p) => ({ ...p, scope_entity_id: e.target.value }))} required />
                  <Input label={t('fiscalization.policies.scopeEntityType')} value={form.scope_entity_type} onChange={(e) => setForm((p) => ({ ...p, scope_entity_type: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input label={t('fiscalization.policies.defaultProviderId')} value={form.default_provider_id} onChange={(e) => setForm((p) => ({ ...p, default_provider_id: e.target.value }))} required />
                  <Input label={t('fiscalization.policies.country')} value={form.country_code} onChange={(e) => setForm((p) => ({ ...p, country_code: e.target.value }))} placeholder="DE" />
                </div>
              </>
            )}
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={form.auto_fiscalize} onChange={(e) => setForm((p) => ({ ...p, auto_fiscalize: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
              {t('fiscalization.policies.autoFiscalize')}
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setFormOpen(false)} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted">{t('common.cancel')}</button>
              <Button type="submit" size="sm" isLoading={store.isSaving}>{editing ? t('common.saveChanges') : t('common.create')}</Button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('fiscalization.policies.deleteConfirmTitle')}
        destructive
        isConfirming={store.isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default PoliciesTab;
